/**
 * Личная статистика диспетчера за смену — в шапке пульта.
 *
 * Цифры приходят в снапшоте (deskDailyStatsSchema) и считаются сервером.
 * Проценты — по формулам отчёта «Диспетчеры» (stats.sql.ts) из тех же цифр,
 * что видны рядом: дозвон = уникальные живые номера ÷ уникальные набранные номера, конверсия = лиды ÷ выданные.
 * Другие формулы разошлись бы с отчётом администратора в первый спорный день.
 *
 * «За день» и «Осталось» — просьба диспетчеров 29.08.2026. Вид с иконками и
 * процентами — просьба Evgeniy 30.08.2026: строка текста читалась как
 * служебная, дозвона и конверсии не было видно вовсе.
 */

import {
  HandCoins,
  Inbox,
  PhoneCall,
  PhoneOutgoing,
  Sparkles,
  Timer,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { DeskDailyStats } from '@cc/shared';

import { formatDuration } from '../../shared/lib/format-duration.js';

export interface DayStatsProps {
  /** null — снапшот ещё не пришёл; нули рисовать нельзя, это враньё */
  stats: DeskDailyStats | null;
  /** Сколько номеров выдано за день (line.usedToday); null — данных ещё нет */
  usedToday: number | null;
  /** Сколько карточек сейчас открыто на руках; null — данных ещё нет */
  remaining: number | null;
}

type Tone = 'slate' | 'blue' | 'emerald';

type Chip = {
  title: string;
  value: string;
  /** Процент под цифрой: «52%» у дозвона, «CR 1.8%» у лидов */
  sub?: string;
  /** Подсказка по наведению: из чего сложилась цифра */
  hint?: string;
  icon: LucideIcon;
  tone: Tone;
};

const TONE_ICON: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-500',
  blue: 'bg-blue-50 text-blue-600',
  emerald: 'bg-emerald-50 text-emerald-600',
};

const TONE_VALUE: Record<Tone, string> = {
  slate: 'text-slate-900',
  blue: 'text-blue-700',
  emerald: 'text-emerald-700',
};

export function DayStats({ stats, usedToday, remaining }: DayStatsProps): React.JSX.Element {
  const pct = (num: number, den: number): string =>
    den > 0 ? `${(Math.round((num / den) * 1000) / 10).toLocaleString('ru-RU')}%` : '—';

  const chips: Chip[] = [
    {
      title: 'За день',
      hint: 'Новых номеров выдано вам за день — по ним считается норма. Перезвоны и повторы прошлых дней не входят',
      value: usedToday === null ? '—' : String(usedToday),
      icon: Inbox,
      tone: 'slate',
    },
    {
      title: 'Осталось',
      hint: 'Карточек на руках без метки',
      value: remaining === null ? '—' : String(remaining),
      icon: PhoneOutgoing,
      tone: 'slate',
    },
    {
      title: 'Дозвон по номерам',
      hint: 'Уникальные номера с живым контактом ÷ уникальные набранные номера. Повторы на один номер считаются один раз; АО и тишина не считаются живым контактом',
      // Живые разговоры по меткам, не снятые трубки: «секунды идут, когда
      // автоответчик включается, — это не дозвон» (Екатерина, 31.08.2026).
      // «0 из 0» до первого набора — шум; просто ноль честнее
      value:
        stats === null
          ? '—'
          : stats.uniqueCalledCnt === 0
            ? '0'
            : `${String(stats.uniqueContactCnt)} из ${String(stats.uniqueCalledCnt)}`,
      ...(stats !== null && stats.uniqueCalledCnt > 0
        ? { sub: pct(stats.uniqueContactCnt, stats.uniqueCalledCnt) }
        : {}),
      icon: PhoneCall,
      tone: 'blue',
    },
    {
      title: 'Лиды',
      hint: 'Заявки за день. CR — лиды ÷ уникальные набранные номера; от дозвона — лиды ÷ номера с живым разговором',
      value: stats === null ? '—' : String(stats.leadsCnt),
      // Конверсия от уникальных номеров, как дозвон (11.09.2026, просьба
      // Evgeniy): раньше делилось на выданные карточки, а с кругами один номер
      // выдаётся до четырёх раз — CR занижался в разы
      ...(stats !== null && stats.uniqueCalledCnt > 0
        ? {
            sub:
              `CR ${pct(stats.leadsCnt, stats.uniqueCalledCnt)}` +
              (stats.uniqueContactCnt > 0
                ? ` · от дозвона ${pct(stats.leadsCnt, stats.uniqueContactCnt)}`
                : ''),
          }
        : {}),
      icon: Sparkles,
      tone: 'emerald',
    },
    {
      title: 'Разговоры',
      hint: 'Время в разговоре за день',
      value: stats === null ? '—' : formatDuration(stats.talkSec),
      icon: Timer,
      tone: 'slate',
    },
  ];
  // Заработок — только когда админ задал ставку: нуль при выключенной оплате
  // читался бы как «мне ничего не заплатят»
  if (stats?.earnedRub != null) {
    chips.push({
      title: 'Заработано',
      value: `${stats.earnedRub.toLocaleString('ru-RU')} ₽`,
      icon: HandCoins,
      tone: 'emerald',
    });
  }

  return (
    <div className="flex flex-wrap items-stretch gap-2">
      {chips.map((chip) => (
        <div
          key={chip.title}
          title={chip.hint}
          className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white py-1.5 pr-4 pl-2"
        >
          <span
            className={
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ' +
              TONE_ICON[chip.tone]
            }
            aria-hidden
          >
            <chip.icon className="h-4 w-4" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">
              {chip.title}
            </span>
            <span className="flex items-baseline gap-1.5">
              <span className={'text-base font-extrabold tabular-nums ' + TONE_VALUE[chip.tone]}>
                {chip.value}
              </span>
              {chip.sub !== undefined ? (
                <span className="text-xs font-semibold text-slate-400 tabular-nums">
                  {chip.sub}
                </span>
              ) : null}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}
